import type { ReactElement } from 'react';
import { BuiltInCatalogLink } from '@/components/policylist/components/BuiltInCatalogLink';
import type { PolicySettingVisibilityFlags } from '@/components/policylist/components/policySettingsVisibility';

/**
 * A row the Policies page shows next to an alert's sub-policy that is not a
 * policy setting (a link to a sub-page, a list managed by its own API), so
 * `ALERT_SUB_POLICY_RELATED_SETTINGS` cannot name it.
 */
export interface AlertSubPolicyExtra {
  id: string;
  /** Same gate as the Policies page uses for the row. */
  isVisible: (flags: PolicySettingVisibilityFlags) => boolean;
  render: () => ReactElement;
}

const ALERT_SUB_POLICY_EXTRAS: Readonly<
  Record<string, Readonly<Record<string, readonly AlertSubPolicyExtra[]>>>
> = {
  'extension-security': {
    // KIR-4929: with capability detection on, permission alerts come from the
    // built-in catalog and the org's custom rules; the Policies page shows the
    // catalog link beside Custom Capability Rules (PolicySettingsForm).
    dangerousPermissions: [
      {
        id: 'built-in-catalog',
        isVisible: flags => flags.isCapabilityDetectionEnabled,
        render: () => <BuiltInCatalogLink />,
      },
    ],
  },
};

/**
 * The non-setting rows to render under an alert's sub-policy whose gate is
 * open for these flags, in display order.
 */
export function getAlertSubPolicyExtras(
  policyKey: string,
  subPolicyKey: string | null | undefined,
  flags: PolicySettingVisibilityFlags
): readonly AlertSubPolicyExtra[] {
  if (!subPolicyKey) return [];
  const rows = Object.prototype.hasOwnProperty.call(
    ALERT_SUB_POLICY_EXTRAS,
    policyKey
  )
    ? ALERT_SUB_POLICY_EXTRAS[policyKey]
    : undefined;
  if (!rows || !Object.prototype.hasOwnProperty.call(rows, subPolicyKey)) {
    return [];
  }
  return rows[subPolicyKey].filter(extra => extra.isVisible(flags));
}
